/**
 * JourneyMap - Visual navigation showing all phases/levels
 */

import { useEffect, useState, useRef } from 'react';
import './JourneyMap.css';

export default function JourneyMap({ 
  phases, 
  levels, 
  currentLevelId, 
  completedLevels, 
  onSelectLevel, 
  onClose,
  devMode 
}) {
  const [mounted, setMounted] = useState(false);
  const currentRef = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
    
    // Scroll to current level
    setTimeout(() => {
      currentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  }, []);

  const getLevelsForPhase = (phaseId) => {
    return levels.filter(l => l.phase === phaseId);
  };

  const isLevelUnlocked = (levelId) => {
    if (devMode) return true;
    if (levelId === 1) return true;
    return completedLevels.includes(levelId - 1);
  };

  const isLevelComplete = (levelId) => {
    return completedLevels.includes(levelId);
  };

  return (
    <div className={`journey-map ${mounted ? 'mounted' : ''}`}>
      {/* Header */}
      <div className="journey-map__header">
        <h1 className="journey-map__title">Journey Map</h1>
        <button className="journey-map__close" onClick={onClose}>✕</button>
      </div>

      {/* Map content */}
      <div className="journey-map__content">
        {phases.map((phase, phaseIndex) => (
          <div key={phase.id} className="journey-map__phase">
            {/* Phase header */}
            <div 
              className="journey-map__phase-header"
              style={{ '--phase-color': phase.color }}
            >
              <span className="journey-map__phase-icon">{phase.icon}</span>
              <div className="journey-map__phase-info">
                <span className="journey-map__phase-number">Phase {phase.id}</span>
                <span className="journey-map__phase-name">{phase.name}</span>
              </div>
            </div>

            {/* Levels */}
            <div className="journey-map__levels">
              {getLevelsForPhase(phase.id).map((level) => {
                const unlocked = isLevelUnlocked(level.id);
                const complete = isLevelComplete(level.id);
                const current = level.id === currentLevelId;

                return (
                  <button
                    key={level.id}
                    ref={current ? currentRef : null}
                    className={`journey-map__level 
                      ${unlocked ? 'journey-map__level--unlocked' : 'journey-map__level--locked'}
                      ${complete ? 'journey-map__level--complete' : ''}
                      ${current ? 'journey-map__level--current' : ''}
                    `}
                    onClick={() => unlocked && onSelectLevel(level.id)}
                    disabled={!unlocked}
                    style={{ '--phase-color': phase.color }}
                  >
                    <span className="journey-map__level-number">{level.id}</span>
                    <span className="journey-map__level-name">{level.name}</span>
                    <span className="journey-map__level-type">
                      {level.type === 'minigame' ? '🎮' : '🏠'}
                    </span>
                    {complete && <span className="journey-map__level-check">✓</span>}
                    {!unlocked && <span className="journey-map__level-lock">🔒</span>}
                  </button>
                );
              })}
            </div>

            {/* Connector line */}
            {phaseIndex < phases.length - 1 && (
              <div className="journey-map__connector" />
            )}
          </div>
        ))}
      </div>

      {/* Progress footer */}
      <div className="journey-map__footer">
        <div className="journey-map__progress">
          <div 
            className="journey-map__progress-fill"
            style={{ width: `${(completedLevels.length / levels.length) * 100}%` }}
          />
        </div>
        <span className="journey-map__progress-text">
          {completedLevels.length} / {levels.length} Complete
        </span>
      </div>
    </div>
  );
}
